// components/widgets/widget-row.js
// ═══════════════════════════════════════════════════════════
// WIDGET ROW — horizontal strip of widget cards sharing width
// ═══════════════════════════════════════════════════════════
// Renders: [Card] [Divider] [Card] [Divider] [Card]
// Each card's width = share proportional to the row total.
// Uses @dnd-kit SortableContext for same-row reorder.
// ═══════════════════════════════════════════════════════════

import { useRef } from 'react';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import WidgetCard from './widget-card';
import WidgetDivider from './widget-divider';

export default function WidgetRow({
  row,
  workstation,
  userId,
  userAiName = null,
  editing = false,
  onRemoveWidget,
  onAdjustShare,
}) {
  const containerRef = useRef(null);
  const itemIds = row.widgets.map((w) => w.key);

  return (
    <div
      ref={containerRef}
      className={`ws-widget-row ${editing ? 'is-editing' : ''}`}
      data-row-id={row.id}
    >
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        {row.widgets.map((widget, idx) => {
          const isLast = idx === row.widgets.length - 1;
          const next = !isLast ? row.widgets[idx + 1] : null;
          return (
            <WidgetInRow
              key={widget.key}
              widget={widget}
              nextWidget={next}
              rowId={row.id}
              workstation={workstation}
              userId={userId}
              userAiName={userAiName}
              editing={editing}
              onRemove={onRemoveWidget}
              onAdjustShare={onAdjustShare}
              containerRef={containerRef}
            />
          );
        })}
      </SortableContext>
    </div>
  );
}

function WidgetInRow({
  widget,
  nextWidget,
  rowId,
  workstation,
  userId,
  userAiName = null,
  editing,
  onRemove,
  onAdjustShare,
  containerRef,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.key, disabled: !editing });

  const style = {
    flex: `${widget.share} ${widget.share} 0`,
    minWidth: 0,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <>
      <WidgetCard
        ref={setNodeRef}
        widgetKey={widget.key}
        workstation={workstation}
        userId={userId}
        userAiName={userAiName}
        editing={editing}
        dragAttributes={attributes}
        dragListeners={listeners}
        isDragging={isDragging}
        onRemove={onRemove}
        style={style}
      />
      {nextWidget && (
        <WidgetDivider
          editing={editing}
          rowId={rowId}
          leftKey={widget.key}
          rightKey={nextWidget.key}
          containerRef={containerRef}
          onAdjust={onAdjustShare}
        />
      )}
    </>
  );
}
